// Promo tab of the event detail page: the deals on this event.
//
// Three kinds (migrations 0035, 0039). Products are picked by searching the
// event's own stock — a promo can only ever apply to what is on the table, so
// the catalogue is not offered at all. A bundle gives each product a quantity;
// a buy-N-get-M gives each product its own N and M.

import { useMemo, useState } from 'react';
import { Gift, Percent, Plus, Pencil, Trash2, Power, Search, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useEventPromos, useSavePromo, useSetPromoActive, useDeletePromo } from '@/hooks/useEventPromos';
import { useEventAllocations } from '@/hooks/useEventAllocations';
import { groupAllocationsByProduct, type AllocationGroup } from '@/lib/allocationGroups';
import { cn, formatIDR, formatNumber } from '@/lib/format';
import type { EventPromo, PromoItem, PromoKind } from '@/types/domain';

const KIND_LABEL: Record<PromoKind, string> = {
  bundle: 'Bundling',
  bogo: 'Beli N gratis M',
  double_discount: 'Diskon bertingkat',
};

function describe(p: EventPromo, productName: (id: string) => string): string {
  if (p.kind === 'bundle') {
    return `${p.items.map((i) => `${i.qty}× ${productName(i.productId)}`).join(' + ')} = ${formatIDR(Number(p.config.bundle_price ?? 0))}`;
  }
  if (p.kind === 'bogo') {
    return p.items
      .map((i) => `${productName(i.productId)}: beli ${i.buyQty ?? p.config.buy_qty ?? '?'} gratis ${i.getQty ?? p.config.get_qty ?? '?'}`)
      .join(' · ');
  }
  const steps = Array.isArray(p.config.steps) ? (p.config.steps as number[]) : [];
  const eff = 100 * (1 - steps.reduce((f, s) => f * (1 - Number(s) / 100), 1));
  return `${steps.map((s) => `${s}%`).join(' + ')} berurutan = ${eff.toFixed(eff % 1 ? 1 : 0)}% dari subtotal`;
}

export function PromoManager({ eventId, editable }: { eventId: string; editable: boolean }) {
  const { data: promos, isLoading } = useEventPromos(eventId);
  const { data: allocations } = useEventAllocations(eventId);
  const setActive = useSetPromoActive();
  const remove = useDeletePromo();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EventPromo | null>(null);
  const [deleting, setDeleting] = useState<EventPromo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stock = useMemo(() => groupAllocationsByProduct(allocations ?? []), [allocations]);
  const stockById = useMemo(() => new Map(stock.map((g) => [g.productId, g])), [stock]);
  const productName = (id: string) => stockById.get(id)?.productName ?? '(tidak ada di stok event)';

  return (
    <>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-subhead font-display font-semibold text-ink">Promo event</h2>
          <p className="text-caption text-ink-subtle">
            Kasir menyalakan promo di langkah keranjang; sistem yang menghitung. Urutan: bundling & beli-gratis per
            baris, lalu diskon bertingkat, lalu diskon manual kasir, lalu PPN.
          </p>
        </div>
        {editable && (
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => { setEditing(null); setFormOpen(true); }}
            disabled={stock.length === 0}
            title={stock.length === 0 ? 'Isi stok event dulu' : undefined}
          >
            Promo baru
          </Button>
        )}
      </div>
      {error && <p role="alert" className="mb-3 text-caption text-danger">{error}</p>}

      {isLoading ? (
        <p className="text-caption text-ink-subtle">Memuat…</p>
      ) : !promos || promos.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Gift size={24} strokeWidth={1.5} />}
            title="Belum ada promo"
            description={
              stock.length === 0
                ? 'Isi stok event dulu di tab Stok — promo hanya bisa memakai produk yang ada di event.'
                : 'Bundling, beli N gratis M, atau diskon bertingkat (30% + 5%) untuk event ini.'
            }
          />
        </Card>
      ) : (
        <ul className="space-y-2">
          {promos.map((p) => {
            const missing = p.items.filter((i) => !stockById.has(i.productId));
            return (
              <li key={p.id}>
                <Card padding="md" className={cn(!p.isActive && 'opacity-60')}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {p.kind === 'double_discount' ? <Percent size={14} className="text-ink-subtle" /> : <Gift size={14} className="text-ink-subtle" />}
                        <p className="text-body font-medium text-ink">{p.name}</p>
                        <Chip variant="default">{KIND_LABEL[p.kind]}</Chip>
                        <Chip variant={p.isActive ? 'success' : 'default'}>{p.isActive ? 'Aktif' : 'Nonaktif'}</Chip>
                      </div>
                      <p className="text-caption text-ink-subtle mt-1">{describe(p, productName)}</p>
                      {missing.length > 0 && (
                        <p className="text-caption text-warning mt-1">
                          {missing.length} produk sudah tidak ada di stok event — promo tidak akan berlaku untuk produk itu.
                        </p>
                      )}
                    </div>
                    {editable && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" title={p.isActive ? 'Nonaktifkan' : 'Aktifkan'} aria-label="Toggle aktif"
                          onClick={() => setActive.mutateAsync({ id: p.id, eventId, isActive: !p.isActive }).catch((e) => setError(e.message))}>
                          <Power size={14} className={p.isActive ? 'text-success' : 'text-ink-subtle'} />
                        </Button>
                        <Button variant="ghost" size="sm" title="Edit" aria-label="Edit" onClick={() => { setEditing(p); setFormOpen(true); }}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" title="Hapus" aria-label="Hapus" onClick={() => setDeleting(p)}>
                          <Trash2 size={14} className="text-danger" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {formOpen && (
        <PromoForm eventId={eventId} promo={editing} stock={stock} onClose={() => setFormOpen(false)} />
      )}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Hapus promo?"
        description={deleting ? `"${deleting.name}" dihapus dari event ini. Kalau sudah pernah dipakai di invoice, hapus akan ditolak — nonaktifkan saja.` : ''}
        confirmText="Hapus"
        variant="danger"
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync({ id: deleting.id, eventId });
            setDeleting(null);
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Gagal menghapus');
            setDeleting(null);
          }
        }}
      />
    </>
  );
}

function PromoForm({
  eventId,
  promo,
  stock,
  onClose,
}: {
  eventId: string;
  promo: EventPromo | null;
  stock: AllocationGroup[];
  onClose: () => void;
}) {
  const save = useSavePromo();
  const [kind, setKind] = useState<PromoKind>(promo?.kind ?? 'bundle');
  const [name, setName] = useState(promo?.name ?? '');
  const [bundlePrice, setBundlePrice] = useState(String(promo?.config.bundle_price ?? ''));
  const [steps, setSteps] = useState<string>(
    Array.isArray(promo?.config.steps) ? (promo!.config.steps as number[]).join(', ') : '30, 5',
  );
  // Chosen products, in the order picked. Rules default to the promo-level
  // config for promos saved before rules moved onto the item.
  const [items, setItems] = useState<PromoItem[]>(
    (promo?.items ?? []).map((i) => ({
      ...i,
      buyQty: i.buyQty ?? (Number(promo?.config.buy_qty) || 2),
      getQty: i.getQty ?? (Number(promo?.config.get_qty) || 1),
    })),
  );
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const byId = useMemo(() => new Map(stock.map((g) => [g.productId, g])), [stock]);
  const chosen = new Set(items.map((i) => i.productId));

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stock
      .filter((g) => !chosen.has(g.productId))
      .filter((g) => !q || g.productName.toLowerCase().includes(q) || g.productSku.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock, search, items]);

  const add = (g: AllocationGroup) => {
    setItems((xs) => [...xs, { productId: g.productId, qty: 1, buyQty: 2, getQty: 1 }]);
    setSearch('');
  };
  const update = (productId: string, patch: Partial<PromoItem>) =>
    setItems((xs) => xs.map((x) => (x.productId === productId ? { ...x, ...patch } : x)));
  const removeItem = (productId: string) => setItems((xs) => xs.filter((x) => x.productId !== productId));

  // Priced at the event's own price, which is what the till charges.
  const componentSum = items.reduce((s, i) => s + (byId.get(i.productId)?.productPrice ?? 0) * i.qty, 0);

  const submit = async () => {
    setError(null);
    const n = name.trim();
    if (n.length < 1) return setError('Nama promo wajib diisi');
    let config: Record<string, unknown> = {};
    let out: PromoItem[] = [];
    if (kind === 'bundle') {
      if (items.length < 2) return setError('Bundling butuh minimal 2 produk');
      const price = Math.round(Number(bundlePrice.replace(/[^\d]/g, '')));
      if (!Number.isFinite(price) || price <= 0) return setError('Harga bundling wajib diisi');
      if (price >= componentSum) return setError(`Harga bundling harus di bawah total satuan (${formatIDR(componentSum)})`);
      config = { bundle_price: price };
      out = items.map((i) => ({ productId: i.productId, qty: Math.max(1, Math.floor(i.qty || 1)) }));
    } else if (kind === 'bogo') {
      if (items.length < 1) return setError('Pilih minimal satu produk');
      const bad = items.find((i) => !((i.buyQty ?? 0) >= 1) || !((i.getQty ?? 0) >= 1));
      if (bad) return setError(`Beli dan gratis untuk ${byId.get(bad.productId)?.productName ?? 'produk'} harus ≥ 1`);
      out = items.map((i) => ({ productId: i.productId, qty: 1, buyQty: Math.floor(i.buyQty!), getQty: Math.floor(i.getQty!) }));
    } else {
      const list = steps.split(/[,\s]+/).filter(Boolean).map(Number);
      if (list.length < 1 || list.length > 4 || list.some((s) => !(s > 0 && s <= 100))) {
        return setError('Isi 1–4 persen, masing-masing 0–100. Contoh: 30, 5');
      }
      config = { steps: list };
    }
    try {
      await save.mutateAsync({ id: promo?.id, eventId, kind, name: n, isActive: promo?.isActive ?? true, config, items: out });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan');
    }
  };

  const eff = (() => {
    const list = steps.split(/[,\s]+/).filter(Boolean).map(Number).filter((s) => s > 0 && s <= 100);
    return 100 * (1 - list.reduce((f, s) => f * (1 - s / 100), 1));
  })();

  const numInput = (value: number | undefined, onChange: (v: number) => void, label: string) => (
    <input
      type="number"
      min={1}
      value={value ?? ''}
      onChange={(e) => onChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
      aria-label={label}
      className="w-14 h-9 px-2 text-right rounded border border-line bg-surface text-small tabular-nums focus:outline-none focus:border-primary"
    />
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={promo ? 'Edit promo' : 'Promo baru'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>Batal</Button>
          <Button variant="primary" onClick={submit} loading={save.isPending}>Simpan</Button>
        </>
      }
    >
      <div className="space-y-4">
        {!promo && (
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Jenis promo">
            {(Object.keys(KIND_LABEL) as PromoKind[]).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={kind === k}
                onClick={() => setKind(k)}
                className={cn('rounded border px-3 py-2 text-small text-left', kind === k ? 'border-primary bg-primary/5 text-ink' : 'border-line text-ink-subtle hover:bg-canvas')}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
        )}
        <Input label="Nama promo" value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'bundle' ? 'Paket Lensa + Frame' : kind === 'bogo' ? 'Beli 2 gratis 1' : 'Diskon 30% + 5%'} autoFocus />

        {kind === 'double_discount' && (
          <Input
            label="Tahapan diskon (persen, berurutan)"
            value={steps}
            onChange={(e) => setSteps(e.target.value)}
            placeholder="30, 5"
            hint={`30% lalu 5% dari sisanya = ${eff.toFixed(eff % 1 ? 1 : 0)}% total. Dihitung dari subtotal setelah bundling/beli-gratis.`}
          />
        )}

        {(kind === 'bundle' || kind === 'bogo') && (
          <div>
            <p className="text-small font-medium text-ink mb-1.5">
              {kind === 'bundle' ? 'Isi bundling' : 'Produk & aturan beli-gratis'}
            </p>

            {/* Chosen products */}
            {items.length > 0 && (
              <ul className="rounded border border-line divide-y divide-line mb-2">
                {items.map((i) => {
                  const g = byId.get(i.productId);
                  return (
                    <li key={i.productId} className="flex items-center gap-3 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-small text-ink truncate" title={g?.productName}>
                          {g?.productName ?? '(tidak ada di stok event)'}
                        </p>
                        <p className="text-caption text-ink-subtle">
                          <code className="font-mono">{g?.productSku}</code>
                          {g && <> · {formatIDR(g.productPrice)} · stok {formatNumber(g.available)}</>}
                        </p>
                      </div>
                      {kind === 'bundle' ? (
                        <label className="flex items-center gap-1.5 text-caption text-ink-subtle shrink-0">
                          Qty {numInput(i.qty, (v) => update(i.productId, { qty: v }), 'Qty dalam bundling')}
                        </label>
                      ) : (
                        <div className="flex items-center gap-1.5 text-caption text-ink-subtle shrink-0">
                          Beli {numInput(i.buyQty, (v) => update(i.productId, { buyQty: v }), 'Beli')}
                          gratis {numInput(i.getQty, (v) => update(i.productId, { getQty: v }), 'Gratis')}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeItem(i.productId)}
                        className="w-8 h-8 rounded inline-flex items-center justify-center text-ink-subtle hover:text-danger hover:bg-danger/5 shrink-0"
                        aria-label="Hapus produk"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Search the event's stock */}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari produk di stok event — nama atau SKU…"
                className="w-full h-10 pl-9 pr-3 rounded border border-line bg-surface text-small text-ink placeholder:text-ink-subtle focus:outline-none focus:border-primary"
              />
            </div>
            <ul className="mt-1.5 max-h-48 overflow-y-auto rounded border border-line divide-y divide-line">
              {matches.length === 0 ? (
                <li className="px-3 py-2 text-caption text-ink-subtle">
                  {stock.length === 0
                    ? 'Stok event masih kosong.'
                    : search
                      ? 'Tidak ada produk stok event yang cocok.'
                      : 'Semua produk di stok event sudah dipilih.'}
                </li>
              ) : (
                matches.map((g) => (
                  <li key={g.productId}>
                    <button
                      type="button"
                      onClick={() => add(g)}
                      className="w-full text-left px-3 py-2 hover:bg-canvas focus:bg-canvas focus:outline-none flex items-center gap-3"
                    >
                      <Plus size={14} className="text-primary shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-small text-ink truncate">{g.productName}</span>
                        <span className="block text-caption text-ink-subtle">
                          <code className="font-mono">{g.productSku}</code> · {formatIDR(g.productPrice)} · {g.lots.length} lot · stok {formatNumber(g.available)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
            <p className="text-caption text-ink-subtle mt-1">
              Hanya produk yang ada di stok event ini. {kind === 'bogo' && 'Unit gratis tetap memotong stok lot.'}
            </p>
          </div>
        )}

        {kind === 'bundle' && (
          <Input
            label="Harga bundling"
            inputMode="numeric"
            value={bundlePrice}
            onChange={(e) => setBundlePrice(e.target.value.replace(/[^\d]/g, ''))}
            hint={componentSum > 0 ? `Total satuan ${formatIDR(componentSum)} — hemat ${formatIDR(Math.max(0, componentSum - Number(bundlePrice || 0)))} per paket.` : 'Harga satu paket lengkap.'}
          />
        )}

        {error && <p role="alert" className="text-caption text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
